import { useEffect, useState } from "react";
import { useRoute, Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { customFetch, storeSessionId } from "@workspace/api-client-react";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandTile } from "@/components/BrandTile";

export default function MagicLinkVerify() {
  const [, params] = useRoute("/entrar/:token");
  const token = params?.token;
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const { loginWithSession } = useAuth();

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("Link de acesso inválido.");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await customFetch<{ user: any; store: any; sessionId: string }>(
          `/api/auth/verify-magic-link/${token}`,
          { method: "GET" }
        );
        if (cancelled) return;
        setStatus("success");
        setTimeout(() => {
          loginWithSession(result);
        }, 1000);
      } catch (err: any) {
        if (cancelled) return;
        setStatus("error");
        setErrorMsg(err?.data?.message || "Link de acesso inválido ou expirado.");
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-[100svh] w-full flex items-center justify-center bg-background px-4">
      <div className="max-w-sm w-full text-center space-y-6 animate-in fade-in duration-500">
        <BrandTile size="lg" className="mx-auto shadow-lg shadow-primary/25" />

        {status === "loading" && (
          <div className="space-y-4">
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
            <div>
              <h2 className="text-xl font-bold">Verificando acesso...</h2>
              <p className="text-muted-foreground mt-1">Aguarde um momento.</p>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-green-700">Acesso confirmado!</h2>
              <p className="text-muted-foreground mt-1">Redirecionando para o painel...</p>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6">
            <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-700">Link inválido</h2>
              <p className="text-muted-foreground mt-1">{errorMsg}</p>
            </div>
            <Link href="/login">
              <Button className="w-full">Solicitar novo acesso</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
